// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The folder store — the abstraction the sync engine reads from and writes to.
 *
 *  • DirectoryStore uses the File System Access API to mirror a real on-disk
 *    folder (Chromium). scan() walks it, read() streams a file out, write()
 *    creates directories and writes bytes back — the live-mirror path.
 *  • FilesStore is the fallback for browsers without a directory picker: it
 *    reads from files the user picked (read-only) and "writes" by triggering a
 *    download. Best for one-way send or receive; there is no on-disk mirror.
 *
 * All writes go through pathsafe.safeSegments first, so a remote peer can never
 * make us escape the chosen folder.
 */
import { safeSegments } from './pathsafe.ts';
import type { Manifest } from './types.ts';

declare global {
  interface Window {
    showDirectoryPicker?: (opts?: { mode?: 'read' | 'readwrite'; id?: string }) => Promise<FileSystemDirectoryHandle>;
  }
}

export interface FolderStore {
  readonly kind: 'directory' | 'files';
  readonly label: string;
  /** Rebuild the manifest of everything currently in the folder. */
  scan(): Promise<Manifest>;
  /** Read one file's bytes + mtime, or null if it has since vanished. */
  read(path: string): Promise<{ bytes: Uint8Array; mtime: number } | null>;
  /** Persist bytes for a path (creating folders), or download them in fallback mode. */
  write(path: string, bytes: Uint8Array, mtime: number): Promise<void>;
}

export function supportsDirectoryPicker(): boolean {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

/** Prompt for a folder and wrap it. Must be called from a user gesture. */
export async function pickDirectory(): Promise<DirectoryStore> {
  if (!window.showDirectoryPicker) throw new Error('Folder picking is not supported in this browser.');
  const handle = await window.showDirectoryPicker({ mode: 'readwrite', id: 'syncwell-folder' });
  return new DirectoryStore(handle);
}

const SKIP_DIRS = new Set(['.git', 'node_modules', '.DS_Store', '.svn', '.hg']);

export class DirectoryStore implements FolderStore {
  readonly kind = 'directory' as const;
  readonly label: string;
  constructor(private root: FileSystemDirectoryHandle) {
    this.label = root.name || 'folder';
  }

  async scan(): Promise<Manifest> {
    const out: Manifest = new Map();
    await this.walk(this.root, '', out);
    return out;
  }

  private async walk(dir: FileSystemDirectoryHandle, prefix: string, out: Manifest): Promise<void> {
    // @ts-expect-error — entries() is present on the handle at runtime.
    for await (const [name, handle] of dir.entries() as AsyncIterable<[string, FileSystemHandle]>) {
      if (SKIP_DIRS.has(name)) continue;
      const path = prefix ? `${prefix}/${name}` : name;
      if (handle.kind === 'file') {
        try {
          const file = await (handle as FileSystemFileHandle).getFile();
          out.set(path, { path, size: file.size, mtime: Math.floor(file.lastModified) });
        } catch {
          /* file locked or removed mid-scan — skip */
        }
      } else {
        await this.walk(handle as FileSystemDirectoryHandle, path, out);
      }
    }
  }

  async read(path: string): Promise<{ bytes: Uint8Array; mtime: number } | null> {
    const segs = safeSegments(path);
    if (!segs) return null;
    try {
      let dir = this.root;
      for (let i = 0; i < segs.length - 1; i++) dir = await dir.getDirectoryHandle(segs[i]);
      const fh = await dir.getFileHandle(segs[segs.length - 1]);
      const file = await fh.getFile();
      const buf = await file.arrayBuffer();
      return { bytes: new Uint8Array(buf), mtime: Math.floor(file.lastModified) };
    } catch {
      return null;
    }
  }

  async write(path: string, bytes: Uint8Array): Promise<void> {
    const segs = safeSegments(path);
    if (!segs) throw new Error(`unsafe path rejected: ${path}`);
    let dir = this.root;
    for (let i = 0; i < segs.length - 1; i++) dir = await dir.getDirectoryHandle(segs[i], { create: true });
    const fh = await dir.getFileHandle(segs[segs.length - 1], { create: true });
    const writable = await fh.createWritable();
    try {
      await writable.write(bytes as unknown as BufferSource);
    } finally {
      await writable.close();
    }
  }
}

/**
 * In-memory store — used only by the automated two-tab verification harness to
 * drive the real sync engine without a native folder picker. Not wired into any
 * user-facing path.
 */
export class MemStore implements FolderStore {
  readonly kind = 'directory' as const;
  readonly label: string;
  private files: Map<string, { bytes: Uint8Array; mtime: number }>;
  constructor(label: string, initial: Record<string, { bytes: Uint8Array; mtime: number }> = {}) {
    this.label = label;
    this.files = new Map(Object.entries(initial));
  }
  async scan(): Promise<Manifest> {
    const out: Manifest = new Map();
    for (const [path, f] of this.files) out.set(path, { path, size: f.bytes.length, mtime: f.mtime });
    return out;
  }
  async read(path: string): Promise<{ bytes: Uint8Array; mtime: number } | null> {
    const f = this.files.get(path);
    return f ? { bytes: f.bytes, mtime: f.mtime } : null;
  }
  async write(path: string, bytes: Uint8Array, mtime: number): Promise<void> {
    if (!safeSegments(path)) throw new Error(`unsafe path rejected: ${path}`);
    this.files.set(path, { bytes, mtime });
  }
  snapshot(): Record<string, { size: number; text: string; mtime: number }> {
    const out: Record<string, { size: number; text: string; mtime: number }> = {};
    for (const [p, f] of this.files) out[p] = { size: f.bytes.length, text: new TextDecoder().decode(f.bytes), mtime: f.mtime };
    return out;
  }
}

/** Fallback store: read from picked files, "write" by downloading. */
export class FilesStore implements FolderStore {
  readonly kind = 'files' as const;
  readonly label: string;
  private files = new Map<string, File>();

  constructor(files: File[]) {
    for (const f of files) {
      const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
      const path = rel && rel.length > 0 ? rel.replace(/^[^/]+\//, '') : f.name;
      if (safeSegments(path)) this.files.set(path, f);
    }
    this.label = files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'}` : 'no files';
  }

  async scan(): Promise<Manifest> {
    const out: Manifest = new Map();
    for (const [path, f] of this.files) out.set(path, { path, size: f.size, mtime: Math.floor(f.lastModified) });
    return out;
  }

  async read(path: string): Promise<{ bytes: Uint8Array; mtime: number } | null> {
    const f = this.files.get(path);
    if (!f) return null;
    const buf = await f.arrayBuffer();
    return { bytes: new Uint8Array(buf), mtime: Math.floor(f.lastModified) };
  }

  async write(path: string, bytes: Uint8Array): Promise<void> {
    // No disk access — deliver as a download instead.
    const safe = safeSegments(path);
    const name = safe ? safe[safe.length - 1] : 'download';
    const blob = new Blob([bytes as unknown as BlobPart]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}
